import { Tooltip } from '@mui/material'
import { formatMoney } from '@zealth/core/formatting'
import React, { forwardRef } from 'react'

interface MoneyProps {
  amount: number
  as?: React.ElementType
  className?: string
  style?: React.CSSProperties
  children?: never
  rawValue?: number
  showTooltip?: boolean
}

/**
 * Money component for displaying formatted USD currency values
 * Renders a span by default, but can render any component via the 'as' prop
 *
 * @example
 * // Basic usage with span (default)
 * <Money amount={1234.56} />
 *
 * @example
 * // With MUI Typography
 * <Money amount={1234.56} as={Typography} variant="h6" />
 *
 * @example
 * // Custom styling
 * <Money amount={1234.56} className="text-green-500" style={{ fontWeight: 'bold' }} />
 *
 * @example
 * // Negative amount with parentheses (default)
 * <Money amount={-1234.56} />
 *
 */
const Money = forwardRef<HTMLSpanElement, MoneyProps>(
  (
    {
      amount,
      as: Component = 'span',
      className,
      style,
      rawValue,
      showTooltip = false,
      ...props
    },
    ref
  ) => {
    const formattedAmount = formatMoney(amount)

    const shouldShowTooltip =
      showTooltip && rawValue !== undefined && rawValue !== amount

    const content = (
      <Component ref={ref} className={className} style={style} {...props}>
        {formattedAmount}
      </Component>
    )

    if (shouldShowTooltip) {
      const rawFormatted = formatMoney(rawValue)

      return (
        <Tooltip title={`Raw value: ${rawFormatted}`} arrow>
          {content}
        </Tooltip>
      )
    }

    return content
  }
)

Money.displayName = 'Money'

export default Money
